import { DatastoreInterface } from '@freelancer/datastore/core';
import { generateId } from '@freelancer/datastore/core/fake/mocks/random';
import { camelCase } from '@freelancer/datastore/core/fake/mocks/textCorpus';
import { EnterpriseApi, PoolApi, RoleApi } from 'api-typings/common/common';
import {
  OnlineOfflineStatusApi,
  UserChosenRoleApi,
} from 'api-typings/users/users';
import {
  CurrencyCode,
  generateCurrencyObject,
} from '../currencies/currencies.mock';
import { UserStatus } from '../project-view-users/user-status.model';
import { User } from './users.model';
import { UsersCollection } from './users.types';

export interface GenerateUsersOptions {
  readonly names: ReadonlyArray<string>;
  readonly enterpriseIds?: ReadonlyArray<EnterpriseApi>;
  readonly role?: RoleApi;
  readonly chosenRole?: UserChosenRoleApi;
  readonly currencyCode?: CurrencyCode;
}

export async function createUsers(
  datastore: DatastoreInterface,
  {
    names,
    enterpriseIds,
    role,
    chosenRole,
    currencyCode,
  }: GenerateUsersOptions,
) {
  const objects = names.map(name =>
    generateUserObject({
      userId: generateId(),
      username: camelCase(name),
      displayName: name,
      enterpriseIds,
      role,
      chosenRole,
      currencyCode,
    }),
  );

  await Promise.all(
    objects.map(object =>
      datastore.createDocument<UsersCollection>('users', object),
    ),
  );

  return objects;
}

export interface GenerateUserOptions {
  readonly userId?: number;
  readonly username?: string;
  readonly displayName?: string;
  readonly enterpriseIds?: ReadonlyArray<EnterpriseApi>;
  readonly role?: RoleApi;
  readonly chosenRole?: UserChosenRoleApi;
  readonly currencyCode?: CurrencyCode;
  readonly userStatus?: UserStatus;
}

export async function createUser(
  datastore: DatastoreInterface,
  options: GenerateUserOptions,
) {
  const object = generateUserObject(options);
  await datastore.createDocument<UsersCollection>('users', object);
  return object;
}

export function generateUserObject({
  userId,
  username = 'testUsername',
  displayName = 'Test Name',
  enterpriseIds = [],
  role = RoleApi.EMPLOYER,
  chosenRole,
  currencyCode = CurrencyCode.USD,
  userStatus = {
    paymentVerified: true,
    emailVerified: true,
    depositMade: true,
    profileComplete: true,
    phoneVerified: true,
    identityVerified: false,
    facebookConnected: false,
  },
}: GenerateUserOptions = {}): User {
  const id = userId !== undefined ? userId : generateId();
  const generatedChosenRole =
    chosenRole !== undefined
      ? chosenRole
      : role
      ? UserChosenRoleApi.EMPLOYER
      : UserChosenRoleApi.FREELANCER;

  return {
    id,
    avatar: generateAvatar(id),
    avatarLarge: generateAvatar(id, 280),
    username,
    closed: false,
    displayName,
    role,
    chosenRole: generatedChosenRole,
    profileUrl: `/u/${username}`,
    onlineOfflineStatus: { status: OnlineOfflineStatusApi.OFFLINE },
    currency: generateCurrencyObject(currencyCode),
    status: userStatus,
    enterpriseIds,
    // from transformer
    isDeloitteDcUser:
      enterpriseIds && enterpriseIds.includes(EnterpriseApi.DELOITTE_DC),
    poolIds: [PoolApi.FREELANCER],
    escrowComInteractionRequired: false,
  };
}

export function generateAvatar(id: number, size = 100) {
  return `assets/bits/avatars/32-${(id % 7) + 1}.jpg`; // TODO: Larger/more avatars
}
